import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import InputAdornment from "@mui/material/InputAdornment";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PageErrorAlert, { PAGE_ERROR_TITLES } from "components/PageErrorAlert";
import { loadWhatsAppInboxForCustomer } from "layouts/whatsapp-inbox/data/whatsappInboxData";
import {
  getWhatsAppApiConfigStatus,
  looksLikeRawWaId,
  normalizeWaId,
} from "utils/whatsappApi";
import { formatPhoneNumberForDisplay } from "utils/phoneUtils";
import {
  fetchCustomerMergedContacts,
  isValidCustomerId,
  parseCustomerId,
} from "utils/customerUtils";

import { formatDateTime } from "utils/dateFormatter";

const ROWS_PER_PAGE = 10;

function mapInboxRowToDisplay(row, customerId) {
  const waId = normalizeWaId(row.wa_id || "");
  const profileLabel =
    row.profile_name && !looksLikeRawWaId(row.profile_name) ? row.profile_name : null;
  const contactName = row.contact_name || profileLabel || formatPhoneNumberForDisplay(waId) || waId;
  const linkedCustomers = row.linked_customers || row.customers || [];
  const rawLatest = row.latest_message_text;
  const messageText =
    rawLatest != null && String(rawLatest).trim() !== ""
      ? String(rawLatest)
      : "";

  return {
    waId,
    contactName,
    messageText,
    messageTime: row.latest_message || "",
    direction: row.latest_message_direction || "outbound",
    hasUnread: row.has_unread === true || Number(row.unread_count) > 0,
    unreadCount: Number(row.unread_count) || 0,
    quotedMessageId: row.quoted_message_id ?? null,
    profileName: profileLabel,
    latestMediaFileName:
      typeof row.media_file_name === "string" && row.media_file_name.trim()
        ? row.media_file_name.trim()
        : null,
    customerContactId: row.customer_contact_id ?? null,
    linkedCustomers,
    returnState: {
      contactName,
      contact_name: contactName,
      phoneNumber: waId,
      customerContactId: row.customer_contact_id ?? null,
      customer_contact_id: row.customer_contact_id ?? null,
      linkedCustomers,
      linked_customers: linkedCustomers,
      preferred_customer_id: Number(customerId),
      preferredCustomerId: Number(customerId),
    },
  };
}

/**
 * WhatsApp inbox scoped to one customer's linked contacts (embedded in customer-view Messages tab).
 */
function CustomerWhatsAppInboxPanel({ customerId, customerName, customerContacts, returnPath }) {
  const navigate = useNavigate();

  const [crmContacts, setCrmContacts] = useState(customerContacts || []);
  const [inboxRows, setInboxRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [inboxTruncated, setInboxTruncated] = useState(false);

  const parsedCustomerId = parseCustomerId(customerId);

  useEffect(() => {
    if (Array.isArray(customerContacts) && customerContacts.length > 0) {
      setCrmContacts(customerContacts);
    }
  }, [customerContacts]);

  const loadData = useCallback(async () => {
    const cfg = getWhatsAppApiConfigStatus();
    if (!cfg.ok) {
      setError(cfg.message);
      setInboxRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!crmContacts.length && isValidCustomerId(customerId)) {
        const list = await fetchCustomerMergedContacts(customerId);
        if (list.length) setCrmContacts(list);
      }

      const { data: scoped, totalUnreadMessages: unreadTotal, isTruncated } =
        await loadWhatsAppInboxForCustomer(parsedCustomerId);

      setInboxRows(scoped);
      setTotalUnreadMessages(unreadTotal);
      setInboxTruncated(Boolean(isTruncated));
    } catch (err) {
      setError(err?.message || "Failed to load WhatsApp messages for this customer.");
      setInboxRows([]);
      setTotalUnreadMessages(0);
    } finally {
      setLoading(false);
    }
  }, [crmContacts.length, parsedCustomerId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const displayContacts = useMemo(() => {
    const mapped = inboxRows.map((row) => mapInboxRowToDisplay(row, parsedCustomerId));
    if (!searchTerm.trim()) return mapped;
    const term = searchTerm.toLowerCase();
    return mapped.filter((c) => {
      const name = (c.contactName || "").toLowerCase();
      const phone = (c.waId || "").toLowerCase();
      const message = (c.messageText || "").toLowerCase();
      return name.includes(term) || phone.includes(term) || message.includes(term);
    });
  }, [inboxRows, searchTerm, parsedCustomerId]);

  const totalPages = Math.max(1, Math.ceil(displayContacts.length / ROWS_PER_PAGE));
  const displayedContacts = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    return displayContacts.slice(start, start + ROWS_PER_PAGE);
  }, [displayContacts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, inboxRows.length]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleViewChat = (contact) => {
    const backUrl = returnPath || `/customers/view/${customerId}`;
    navigate(`/whatsapp-chat/${contact.waId}`, {
      state: {
        ...contact.returnState,
        returnUrl: backUrl,
        activeCustomerTab: 2,
        returnState: { activeCustomerTab: 2 },
      },
    });
  };

  const linkedContactsWithWhatsApp = useMemo(
    () =>
      (crmContacts || []).filter((row) => String(row?.whatsapp ?? "").trim().length > 0),
    [crmContacts]
  );

  const hasLinkedContacts = (crmContacts || []).length > 0;
  const canStartTemplateConversation =
    !loading && !error && displayContacts.length === 0 && linkedContactsWithWhatsApp.length > 0;

  const handleStartTemplateConversation = () => {
    const cid = parseCustomerId(customerId);
    if (!isValidCustomerId(customerId)) return;
    const backUrl = returnPath || `/customers/view/${customerId}`;
    navigate("/whatsapp-send-to-one", {
      state: {
        preferred_customer_id: cid,
        preferredCustomerId: cid,
        returnPath: backUrl,
        returnState: { activeCustomerTab: 2 },
      },
    });
  };

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <MDBox>
          <MDTypography variant="h5">WhatsApp messages</MDTypography>
          <MDTypography variant="caption" color="text">
            Conversations with contacts linked to {customerName || "this customer"}.
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="info" size="small" onClick={() => void loadData()} disabled={loading}>
          <Icon>refresh</Icon>&nbsp;Refresh
        </MDButton>
      </MDBox>

      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <MDBox p={2} pb={1}>
            <MDInput
              fullWidth
              placeholder="Search contacts or messages"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
            {!loading && !error && (
              <MDBox mt={1} display="flex" alignItems="center" gap={0.75}>
                <Icon
                  sx={{
                    fontSize: 18,
                    color: totalUnreadMessages > 0 ? "error.main" : "text.secondary",
                  }}
                >
                  mark_email_unread
                </Icon>
                <MDTypography
                  variant="caption"
                  color={totalUnreadMessages > 0 ? "error" : "secondary"}
                  fontWeight={totalUnreadMessages > 0 ? "medium" : "regular"}
                >
                  {totalUnreadMessages === 0
                    ? "No unread messages in linked conversations"
                    : `${totalUnreadMessages.toLocaleString()} unread in linked conversations`}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>

          {error && (
            <MDBox px={2} pb={2}>
              <PageErrorAlert title={PAGE_ERROR_TITLES.whatsappInbox} message={error} />
            </MDBox>
          )}

          {inboxTruncated && !loading && !error && (
            <MDBox px={2} pb={1}>
              <MDTypography variant="caption" color="warning">
                Inbox results were truncated; some conversations may be missing.
              </MDTypography>
            </MDBox>
          )}

          <MDBox sx={{ minHeight: 280, maxHeight: "min(60vh, 520px)", overflowY: "auto" }}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </MDBox>
            ) : !error && displayContacts.length === 0 ? (
              <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={3} gap={2}>
                <MDTypography variant="body2" color="text" textAlign="center">
                  {hasLinkedContacts
                    ? "No WhatsApp conversations found for contacts linked to this customer."
                    : "No contacts are linked to this customer yet."}
                  <br />
                  {canStartTemplateConversation
                    ? "Start a new thread with an approved template message."
                    : hasLinkedContacts
                      ? "Add WhatsApp numbers on the Contacts tab, then refresh or start below."
                      : "Add contacts on the Contacts tab, then refresh."}
                </MDTypography>
                {canStartTemplateConversation && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleStartTemplateConversation}
                    startIcon={<Icon>description</Icon>}
                  >
                    Start conversation with template
                  </MDButton>
                )}
              </MDBox>
            ) : (
              !error && (
                <TableContainer>
                  <Table>
                    <TableBody>
                      {displayedContacts.map((contact, index) => (
                        <TableRow key={`${contact.waId}-${index}`} hover>
                          <TableCell
                            width="30%"
                            sx={{ padding: "12px", borderBottom: "1px solid #eee", verticalAlign: "top" }}
                          >
                            <MDTypography variant="button" fontWeight="bold" display="block" sx={{ fontSize: "14px" }}>
                              {contact.contactName}
                            </MDTypography>
                            <MDTypography variant="caption" color="dark" sx={{ fontSize: "12px", display: "block" }}>
                              {formatPhoneNumberForDisplay(contact.waId)}
                            </MDTypography>
                          </TableCell>
                          <TableCell
                            width="15%"
                            sx={{
                              padding: "12px",
                              borderBottom: "1px solid #eee",
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <MDButton
                              variant="contained"
                              color="info"
                              size="small"
                              onClick={() => handleViewChat(contact)}
                            >
                              View chat
                            </MDButton>
                          </TableCell>
                          <TableCell
                            width="55%"
                            sx={{ padding: "12px", borderBottom: "1px solid #eee", verticalAlign: "top" }}
                          >
                            {contact.quotedMessageId ? (
                              <MDTypography variant="caption" color="secondary" sx={{ fontSize: "11px", display: "block", mb: 0.5 }}>
                                Reply to an earlier message
                              </MDTypography>
                            ) : null}
                            {contact.hasUnread && (
                              <MDBox
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f44336",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: "20px",
                                  height: "20px",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  marginRight: "8px",
                                }}
                              >
                                {contact.unreadCount > 0 ? contact.unreadCount : 1}
                              </MDBox>
                            )}
                            <MDTypography
                              variant="button"
                              fontWeight={contact.hasUnread ? "bold" : "regular"}
                              color="dark"
                              sx={{ fontSize: "14px", display: "block" }}
                            >
                              {contact.messageText && contact.messageText.length > 100
                                ? `${contact.messageText.substring(0, 100)}…`
                                : contact.messageText || "No message"}
                            </MDTypography>
                            {contact.latestMediaFileName ? (
                              <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                <Icon sx={{ fontSize: 15, opacity: 0.75 }}>attach_file</Icon>
                                <MDTypography variant="caption" color="secondary" sx={{ fontSize: "11px" }}>
                                  {contact.latestMediaFileName}
                                </MDTypography>
                              </MDBox>
                            ) : null}
                            <MDTypography variant="caption" color="dark" sx={{ fontSize: "12px", display: "block" }}>
                              {formatDateTime(contact.messageTime)}
                              {contact.direction === "outbound" ? " ✓" : ""}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </MDBox>

          {!loading && !error && displayContacts.length > 0 && (
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
              sx={{ borderTop: "1px solid #eee" }}
            >
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <Icon>chevron_left</Icon> Previous
              </MDButton>
              <MDTypography variant="button" color="dark">
                Page {currentPage} of {totalPages} ({displayContacts.length} conversations)
              </MDTypography>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next <Icon>chevron_right</Icon>
              </MDButton>
            </MDBox>
          )}
        </CardContent>
      </Card>
    </MDBox>
  );
}

CustomerWhatsAppInboxPanel.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  customerName: PropTypes.string,
  customerContacts: PropTypes.arrayOf(PropTypes.object),
  returnPath: PropTypes.string,
};

CustomerWhatsAppInboxPanel.defaultProps = {
  customerName: "",
  customerContacts: [],
  returnPath: "",
};

export default CustomerWhatsAppInboxPanel;
